/**
 * SSE streaming client for the transaction import endpoint.
 *
 * Posts multipart FormData to /import/transactions/stream and parses the
 * `text/event-stream` response incrementally, dispatching parsed JSON
 * payloads to callbacks:
 *
 *   streamImport(formData, {
 *     onPhase: ({ phase }) => ...,
 *     onProgress: ({ processed, total }) => ...,
 *     onDone: ({ imported, skipped, errors, isSimulation }) => ...,
 *     onError: ({ error }) => ...,
 *   });
 *
 * Uses fetch + ReadableStream (no EventSource — the request is a POST with
 * multipart body). Authentication follows the shared axios instance
 * convention in api.js: raw JWT in the Authorization header, no Bearer
 * prefix, and a 401 clears the token and redirects to the login page.
 */

const PING_PREFIX = ':';

function buildStreamUrl() {
    const base = process.env.REACT_APP_API_URL || '';
    return `${base.replace(/\/$/, '')}/import/transactions/stream`;
}

function handleUnauthorized(response) {
    if (response.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/';
        return true;
    }
    return false;
}

/**
 * Parse one SSE event block (lines separated by \n) into { event, data }.
 * Returns null for comment/keepalive blocks or malformed data lines.
 */
function parseSseBlock(block) {
    let event = null;
    let data = null;
    for (const line of block.split('\n')) {
        if (line.startsWith(PING_PREFIX)) continue;
        if (line.startsWith('event: ')) {
            event = line.slice('event: '.length).trim();
        } else if (line.startsWith('data: ')) {
            data = line.slice('data: '.length);
        }
    }
    if (!event || data === null) return null;
    try {
        return { event, data: JSON.parse(data) };
    } catch (err) {
        return null;
    }
}

async function streamImport(formData, { onPhase, onProgress, onDone, onError } = {}) {
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) {
        headers['Authorization'] = token;
    }

    const response = await fetch(buildStreamUrl(), {
        method: 'POST',
        body: formData,
        headers,
    });

    if (!response.ok) {
        if (handleUnauthorized(response)) return;
        let message = `Import failed (${response.status})`;
        try {
            const payload = await response.json();
            if (payload && payload.error) message = payload.error;
        } catch (err) {
            // response body was not JSON; keep the default message
        }
        if (onError) onError({ error: message });
        return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    const dispatch = (block) => {
        const parsed = parseSseBlock(block);
        if (!parsed) return;
        if (parsed.event === 'phase' && onPhase) onPhase(parsed.data);
        else if (parsed.event === 'progress' && onProgress) onProgress(parsed.data);
        else if (parsed.event === 'done' && onDone) onDone(parsed.data);
        else if (parsed.event === 'error' && onError) onError(parsed.data);
    };

    // SSE frames are separated by a blank line; accumulate until we see one.
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let separatorIndex;
        while ((separatorIndex = buffer.indexOf('\n\n')) !== -1) {
            const block = buffer.slice(0, separatorIndex);
            buffer = buffer.slice(separatorIndex + 2);
            dispatch(block);
        }
    }
    // Flush any trailing block not followed by a blank line.
    const tail = buffer.trim();
    if (tail) dispatch(tail);
}

export { streamImport };